import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import { AvailablePaymentMethodsProvider } from './availablePaymentMethodContext';
import type { PromotePageCombinedState, PromotePagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { useStateInitData } from './hooks/useStateInitData';
import { ModalConfirmationProvider } from './ModalConfirmationContext';
import { PromotePageDataContextProvider } from './pageData';
import { PromotePageStateContextProvider } from './pageState';
import { PromotePageTrackingContextProvider } from './trackingContext';

export type ErrorHandlerProps = { context: ErrorStateDetails<PromotePageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: PromotePagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const PromotePageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(slug, prefetchedData);
	const getStateInitData = useStateInitData();

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;
	const stateInitData = getStateInitData(pageData.data);

	return (
		<PromotePageDataContextProvider pageData={pageData}>
			<PromotePageStateContextProvider {...stateInitData}>
				<AvailablePaymentMethodsProvider>
					<ModalConfirmationProvider>
						<PromotePageTrackingContextProvider>{children}</PromotePageTrackingContextProvider>
					</ModalConfirmationProvider>
				</AvailablePaymentMethodsProvider>
			</PromotePageStateContextProvider>
		</PromotePageDataContextProvider>
	);
};
